import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  FileText, Download, Calendar as CalendarIcon, TrendingUp, 
  Truck, FileSpreadsheet, Package, RefreshCw,
  Building2, User, Mail, Send, Filter, BarChart3,
  CheckCircle2, X, Settings, CalendarRange
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import FinancialAiAnalysisCard from '../components/admin/FinancialAiAnalysisCard';
import OperationalAiAnalysisCard from '../components/admin/OperationalAiAnalysisCard';
import CommercialAiAnalysisCard from '../components/admin/CommercialAiAnalysisCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPage() {
  const { isAuthenticated, user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [anonymizeDataset, setAnonymizeDataset] = useState(false);

  
  // Date range mode: 'monthly' or 'custom'
  const [dateMode, setDateMode] = useState('monthly');
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  
  // Admin filters
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('all');
  
  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [includeCSV, setIncludeCSV] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'operator';
  // Cloison données financières internes (marges, coûts transporteurs, trésorerie)
  const FINANCIAL_ROLES = ['admin', 'super_admin', 'finance', 'auditeur', 'lead_ops', 'lead_commercial', 'commercial'];
  const canSeeFinancialData = FINANCIAL_ROLES.includes(user?.role);
  const canConfigureFinancial = canSeeFinancialData && user?.role !== 'commercial';
  // Analyse opérationnelle : staff interne (exploitation incluse), jamais les clients
  const OPERATIONAL_ROLES = [...FINANCIAL_ROLES, 'ops', 'operator', 'operateur'];
  const canSeeOperationalData = OPERATIONAL_ROLES.includes(user?.role);
  // Rapport commercial : direction uniquement (admin / lead commercial)
  const canSeeCommercialReport = ['admin', 'super_admin', 'lead_commercial'].includes(user?.role);
  
  // SEC-003 — sentinelle (auth réelle via cookie HttpOnly + axios withCredentials)
  const getToken = () => 'cookie-auth';

  // Build query params based on date mode
  const buildDateParams = useCallback(() => {
    if (dateMode === 'custom' && customFrom && customTo) {
      return `start_date=${format(customFrom, 'yyyy-MM-dd')}&end_date=${format(customTo, 'yyyy-MM-dd')}`;
    }
    if (selectedPeriod) {
      return `year=${selectedPeriod.year}&month=${selectedPeriod.month}`;
    }
    return null;
  }, [dateMode, customFrom, customTo, selectedPeriod]);

  // Period display label
  const getPeriodLabel = () => {
    if (dateMode === 'custom' && customFrom && customTo) {
      return `Du ${format(customFrom, 'dd/MM/yyyy')} au ${format(customTo, 'dd/MM/yyyy')}`;
    }
    return selectedPeriod?.label || '-';
  };

  // Fetch available periods
  useEffect(() => {
    const fetchPeriods = async () => {
      const token = getToken();
      if (!token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/reports/available-periods`, {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        });
        const data = await response.json();
        setPeriods(data.periods || []);
        if (data.periods?.length > 0) {
          setSelectedPeriod(data.periods[0]);
        }
      } catch (error) {
        console.error('Error fetching periods:', error);
        toast.error('Erreur lors du chargement des périodes');
      }
    };
    
    if (isAuthenticated) {
      fetchPeriods();
    }
  }, [isAuthenticated]);

  // Fetch companies list (admin only)
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!isAdmin) return;
      
      const token = getToken();
      if (!token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/reports/companies`, {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        });
        if (response.ok) {
          const data = await response.json();
          setCompanies(data.companies || []);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    
    if (isAuthenticated && isAdmin) {
      fetchCompanies();
    }
  }, [isAuthenticated, isAdmin]);

  // Fetch summary when period or filters change
  useEffect(() => {
    const fetchSummary = async () => {
      const dateParams = buildDateParams();
      if (!dateParams) return;
      
      const token = getToken();
      if (!token) return;
      
      setLoading(true);
      try {
        let url = `${API_URL}/api/reports/summary?${dateParams}`;
        
        // Add company filter for admin
        if (isAdmin && selectedCompany && selectedCompany !== 'all') {
          url += `&company_filter=${encodeURIComponent(selectedCompany)}`;
        }
        
        const response = await fetch(url, {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        });
        const data = await response.json();
        setSummary(data);
      } catch (error) {
        console.error('Error fetching summary:', error);
        toast.error('Erreur lors du chargement du résumé');
      } finally {
        setLoading(false);
      }
    };
    
    if (isAuthenticated) {
      fetchSummary();
    }
  }, [isAuthenticated, buildDateParams, selectedCompany, isAdmin]);

  const handleDownload = async (reportType, fmt) => {
    const dateParams = buildDateParams();
    if (!dateParams) return;
    
    const token = getToken();
    if (!token) return;
    
    const key = `${reportType}-${fmt}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    
    try {
      let url = `${API_URL}/api/reports/download/${reportType}/${fmt}?${dateParams}`;
      
      // Add filters for admin
      if (isAdmin) {
        if (selectedCompany && selectedCompany !== 'all') {
          url += `&company_filter=${encodeURIComponent(selectedCompany)}`;
        } else if (selectedPartner && selectedPartner !== 'all') {
          url += `&partner_id=${encodeURIComponent(selectedPartner)}`;
        }
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `Rapport_${reportType}_${getPeriodLabel().replace(/\//g, '-').replace(/\s+/g, '_')}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
      
      toast.success(`${fmt.toUpperCase()} téléchargé avec succès`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDownloadDataset = async () => {
    const dateParams = buildDateParams();
    if (!dateParams) return;
    setDownloading(prev => ({ ...prev, 'financial-dataset': true }));
    try {
      let url = `${API_URL}/api/reports/download/financial-dataset/xlsx?${dateParams}&anonymize=${anonymizeDataset}`;
      if (isAdmin && selectedCompany && selectedCompany !== 'all') {
        url += `&company_filter=${encodeURIComponent(selectedCompany)}`;
      }
      const response = await fetch(url, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `Dossier_Analyse_Financiere_${getPeriodLabel().replace(/\//g, '-').replace(/\s+/g, '_')}${anonymizeDataset ? '_anonymise' : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
      toast.success('Dossier d\'analyse financière téléchargé');
    } catch (error) {
      console.error('Dataset download error:', error);
      toast.error('Erreur lors de la génération du dossier');
    } finally {
      setDownloading(prev => ({ ...prev, 'financial-dataset': false }));
    }
  };

  const handleDownloadPerformance = async () => {
    const dateParams = buildDateParams();
    if (!dateParams) return;
    
    const token = getToken();
    if (!token) return;
    
    setDownloading(prev => ({ ...prev, 'performance-pdf': true }));
    
    try {
      let url = `${API_URL}/api/reports/download/performance/pdf?${dateParams}`;
      
      if (isAdmin) {
        if (selectedCompany && selectedCompany !== 'all') {
          url += `&company_filter=${encodeURIComponent(selectedCompany)}`;
        } else if (selectedPartner && selectedPartner !== 'all') {
          url += `&partner_id=${encodeURIComponent(selectedPartner)}`;
        }
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      
      const partnerLabel = selectedCompany !== 'all' ? selectedCompany : 
                          selectedPartner !== 'all' ? 'Partenaire' : 'Global';
      a.download = `Rapport_Performance_${partnerLabel.replace(/\s+/g, '_')}_${getPeriodLabel().replace(/\//g, '-').replace(/\s+/g, '_')}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
      
      toast.success('Rapport de Performance téléchargé');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(prev => ({ ...prev, 'performance-pdf': false }));
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipients.trim()) {
      toast.error('Veuillez saisir au moins un destinataire');
      return;
    }
    
    const token = getToken();
    if (!token) return;
    
    const recipients = emailRecipients.split(/[,;\s]+/).filter(e => e.includes('@'));
    
    if (recipients.length === 0) {
      toast.error('Veuillez saisir des adresses email valides');
      return;
    }
    
    setSendingEmail(true);
    
    try {
      const payload = {
        recipient_emails: recipients,
        include_csv: includeCSV
      };

      if (dateMode === 'custom' && customFrom && customTo) {
        payload.start_date = format(customFrom, 'yyyy-MM-dd');
        payload.end_date = format(customTo, 'yyyy-MM-dd');
      } else if (selectedPeriod) {
        payload.year = selectedPeriod.year;
        payload.month = selectedPeriod.month;
      }
      
      if (selectedCompany && selectedCompany !== 'all') {
        payload.company_filter = selectedCompany;
      } else if (selectedPartner && selectedPartner !== 'all') {
        payload.partner_id = selectedPartner;
      }
      
      const response = await fetch(`${API_URL}/api/reports/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer cookie-auth',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'envoi');
      }
      
      const result = await response.json();
      toast.success(result.message || 'Rapport envoyé avec succès');
      setShowEmailModal(false);
      setEmailRecipients('');
    } catch (error) {
      console.error('Email error:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const formatCurrency = (num) => {
    if (num === undefined || num === null) return '0 XOF';
    return new Intl.NumberFormat('fr-FR').format(num) + ' XOF';
  };

  const getSelectedLabel = () => {
    if (selectedCompany && selectedCompany !== 'all') {
      return selectedCompany;
    }
    if (selectedPartner && selectedPartner !== 'all') {
      const p = partners.find(p => p.id === selectedPartner);
      return p?.name || 'Partenaire sélectionné';
    }
    return 'Vue Globale';
  };

  const ReportCard = ({ title, icon: Icon, description, stats, reportType, color = 'green' }) => (
    <Card className="overflow-hidden">
      <CardHeader className={`bg-gradient-to-r ${color === 'green' ? 'from-emerald-50 to-green-50' : color === 'blue' ? 'from-blue-50 to-indigo-50' : 'from-amber-50 to-orange-50'} pb-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${color === 'green' ? 'bg-emerald-100' : color === 'blue' ? 'bg-blue-100' : 'bg-amber-100'}`}>
              <Icon className={`w-5 h-5 ${color === 'green' ? 'text-emerald-600' : color === 'blue' ? 'text-blue-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid grid-cols-2 gap-4 mb-4">
          {stats.map((stat, idx) => (
            <div key={idx} className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-xs text-gray-500 uppercase">{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button 
            variant="default"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => handleDownload(reportType, 'pdf')}
            disabled={downloading[`${reportType}-pdf`]}
            data-testid={`download-${reportType}-pdf`}
          >
            {downloading[`${reportType}-pdf`] ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            PDF
          </Button>
          <Button 
            variant="outline"
            className="flex-1"
            onClick={() => handleDownload(reportType, 'csv')}
            disabled={downloading[`${reportType}-csv`]}
            data-testid={`download-${reportType}-csv`}
          >
            {downloading[`${reportType}-csv`] ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            CSV
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Centre de Rapports</h1>
          <p className="text-gray-500 mt-1">Générez et envoyez vos rapports de performance</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Auto Report Config Button */}
          <Link to="/mes-rapports/configuration">
            <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="config-auto-reports">
              <Settings className="w-4 h-4 mr-2" />
              Rapports automatiques
            </Button>
          </Link>
          
          {/* Date mode toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-testid="date-mode-toggle">
            <button
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${dateMode === 'monthly' ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setDateMode('monthly')}
              data-testid="date-mode-monthly"
            >
              <CalendarIcon className="w-3.5 h-3.5 inline mr-1" />
              Mensuel
            </button>
            <button
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${dateMode === 'custom' ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setDateMode('custom')}
              data-testid="date-mode-custom"
            >
              <CalendarRange className="w-3.5 h-3.5 inline mr-1" />
              Personnalisé
            </button>
          </div>

          {/* Monthly period selector */}
          {dateMode === 'monthly' && (
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <Select
                value={selectedPeriod ? `${selectedPeriod.year}-${selectedPeriod.month}` : ''}
                onValueChange={(value) => {
                  const [year, month] = value.split('-').map(Number);
                  const period = periods.find(p => p.year === year && p.month === month);
                  setSelectedPeriod(period);
                }}
              >
                <SelectTrigger className="w-[180px]" data-testid="period-selector">
                  <SelectValue placeholder="Sélectionner une période" />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((period) => (
                    <SelectItem 
                      key={`${period.year}-${period.month}`} 
                      value={`${period.year}-${period.month}`}
                    >
                      {period.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Custom date range picker */}
          {dateMode === 'custom' && (
            <div className="flex items-center gap-2" data-testid="custom-date-range">
              <Popover open={fromOpen} onOpenChange={setFromOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-[150px] justify-start text-left text-sm ${!customFrom && 'text-muted-foreground'}`}
                    data-testid="date-from-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customFrom ? format(customFrom, 'dd/MM/yyyy') : 'Date début'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customFrom}
                    onSelect={(date) => { setCustomFrom(date); setFromOpen(false); }}
                    disabled={(date) => customTo ? date > customTo : false}
                    locale={fr}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <span className="text-gray-400 text-sm">au</span>
              <Popover open={toOpen} onOpenChange={setToOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-[150px] justify-start text-left text-sm ${!customTo && 'text-muted-foreground'}`}
                    data-testid="date-to-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customTo ? format(customTo, 'dd/MM/yyyy') : 'Date fin'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customTo}
                    onSelect={(date) => { setCustomTo(date); setToOpen(false); }}
                    disabled={(date) => customFrom ? date < customFrom : false}
                    locale={fr}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
          
          {/* Company filter (admin only) */}
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <Select
                value={selectedCompany}
                onValueChange={(value) => {
                  setSelectedCompany(value);
                  if (value !== 'all') setSelectedPartner('all');
                }}
              >
                <SelectTrigger className="w-[200px]" data-testid="company-filter">
                  <SelectValue placeholder="Toutes les entreprises" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les entreprises</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.name} value={company.name}>
                      {company.name} ({company.user_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Period indicator */}
      {(dateMode === 'custom' && customFrom && customTo) && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="custom-period-indicator">
          <CalendarRange className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-blue-800">
            Période sélectionnée : <strong>{getPeriodLabel()}</strong>
          </span>
        </div>
      )}

      {/* Filter indicator */}
      {isAdmin && (selectedCompany !== 'all' || selectedPartner !== 'all') && (
        <div className="mb-6 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Filter className="w-4 h-4 text-emerald-600" />
          <span className="text-sm text-emerald-800">
            Rapport filtré pour : <strong>{getSelectedLabel()}</strong>
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 text-emerald-700 hover:text-emerald-900"
            onClick={() => {
              setSelectedCompany('all');
              setSelectedPartner('all');
            }}
          >
            <X className="w-4 h-4 mr-1" />
            Réinitialiser
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : (
        <>
          {/* Summary Cards - Split Spot / Contrats */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm">Opérations Spot</p>
                      <p className="text-xl md:text-3xl font-bold">{summary.spot?.count || 0}</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-emerald-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Transferts Contrats</p>
                      <p className="text-xl md:text-3xl font-bold">{summary.contractOps?.transfersCount || summary.transfers?.count || 0}</p>
                    </div>
                    <Truck className="w-10 h-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm">Contrats Actifs</p>
                      <p className="text-xl md:text-3xl font-bold">{summary.contractOps?.activeContracts || summary.contracts?.active || 0}</p>
                    </div>
                    <FileText className="w-10 h-10 text-amber-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Tonnage Total</p>
                      <p className="text-xl md:text-3xl font-bold">{formatNumber((summary.spot?.tonnage || 0) + (summary.contractOps?.tonnage || summary.transfers?.tonnage || 0))}t</p>
                    </div>
                    <Package className="w-10 h-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* CLIENT MONTHLY REPORT - Full 6-section PDF */}
          <Card className="mb-8 border-2 border-[#1db584] shadow-lg" data-testid="monthly-report-card">
            <CardHeader className="bg-gradient-to-r from-[#1db584]/10 via-emerald-50 to-green-50">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-[#1db584] to-[#16a075] shadow-lg">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Rapport Mensuel Complet</CardTitle>
                    <CardDescription className="text-base">
                      PDF A4 — 6 sections : Résumé exécutif, Activité, Performance, Finances, Contrats & Flotte
                    </CardDescription>
                  </div>
                </div>
                <Button
                  className="bg-[#1db584] hover:bg-[#16a075] shadow-md"
                  onClick={() => handleDownload('client-monthly', 'pdf')}
                  disabled={downloading['client-monthly-pdf']}
                  data-testid="download-monthly-pdf"
                >
                  {downloading['client-monthly-pdf'] ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Télécharger le rapport
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                {['Résumé exécutif', 'Activité opérationnelle', 'Performance logistique', 'Synthèse financière', 'Contrats & flotte', 'Envoi automatique'].map((s) => (
                  <div key={s} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-[#1db584]" />
                    <span className="text-xs">{s}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Performance Report Card - NEW */}
          <Card className="mb-8 border-2 border-emerald-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Rapport de Performance</CardTitle>
                    <CardDescription className="text-base">
                      PDF paysage avec KPIs, graphiques et tableaux détaillés
                    </CardDescription>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700 shadow-md"
                    onClick={handleDownloadPerformance}
                    disabled={downloading['performance-pdf']}
                    data-testid="download-performance-pdf"
                  >
                    {downloading['performance-pdf'] ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Télécharger PDF
                  </Button>
                  {isAdmin && (
                    <Button 
                      variant="outline"
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => setShowEmailModal(true)}
                      data-testid="send-email-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer par Email
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>Logo Baobab</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>Format Paysage</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>KPIs visuels</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>Graphiques</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>Tableaux détaillés</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>Pied de page</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Standard Report Cards - SPLIT BY CATEGORY */}
          
          {/* SECTION 1: SPOT */}
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Opérations Spot
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <ReportCard
              title="Rapport Spot"
              icon={TrendingUp}
              description="Toutes les opérations spot de la période"
              reportType="operations"
              color="green"
              stats={[
                { value: summary?.spot?.count || summary?.operations?.count || 0, label: 'Opérations' },
                { value: `${formatNumber(summary?.spot?.tonnage || summary?.operations?.tonnage || 0)}t`, label: 'Tonnage' },
                { value: formatNumber(summary?.spot?.distance || summary?.operations?.distance || 0), label: 'Km parcourus' },
                { value: formatCurrency(summary?.spot?.amount || summary?.operations?.amount || 0), label: 'Montant' },
              ]}
            />
            <Card className="flex flex-col justify-center items-center border-dashed border-2 border-gray-200 bg-gray-50/50">
              <CardContent className="text-center py-8">
                <p className="text-sm text-gray-500 mb-1">Taux de complétion spot</p>
                <p className="text-4xl font-bold text-emerald-600">{summary?.spot?.completionRate || 0}%</p>
                <p className="text-xs text-gray-400 mt-2">{summary?.spot?.completed || 0} terminées sur {summary?.spot?.count || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* SECTION 2: CONTRATS */}
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Contrats & Transferts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <ReportCard
              title="Transferts Contrats"
              icon={Truck}
              description="Courses liées aux contrats"
              reportType="transfers"
              color="blue"
              stats={[
                { value: summary?.contractOps?.transfersCount || summary?.transfers?.count || 0, label: 'Transferts' },
                { value: summary?.contractOps?.transfersCompleted || summary?.transfers?.completed || 0, label: 'Terminés' },
                { value: `${formatNumber(summary?.contractOps?.tonnage || summary?.transfers?.tonnage || 0)}t`, label: 'Tonnage' },
                { value: `${summary?.contractOps?.completionRate || 0}%`, label: 'Complétion' },
              ]}
            />
            
            <ReportCard
              title="Contrats"
              icon={FileText}
              description="Synthèse contractuelle"
              reportType="contracts"
              color="amber"
              stats={[
                { value: summary?.contractOps?.activeContracts || summary?.contracts?.active || 0, label: 'Actifs' },
                { value: summary?.contractOps?.totalContracts || summary?.contracts?.total || 0, label: 'Total' },
                { value: summary?.contractOps?.expiringSoon || summary?.contracts?.expiring_soon || 0, label: 'Expirent bientôt' },
                { value: `${formatNumber(summary?.contractOps?.totalTrips || 0)}`, label: 'Rotations' },
              ]}
            />

            <Card className="flex flex-col justify-center items-center border-dashed border-2 border-gray-200 bg-gray-50/50">
              <CardContent className="text-center py-8">
                <p className="text-sm text-gray-500 mb-1">Tonnage net contrats</p>
                <p className="text-4xl font-bold text-blue-600">{formatNumber(summary?.contractOps?.netTonnage || 0)}t</p>
                <p className="text-xs text-gray-400 mt-2">sur {formatNumber(summary?.contractOps?.tonnage || summary?.transfers?.tonnage || 0)}t brut</p>
              </CardContent>
            </Card>
          </div>

          {/* Consolidated Report */}
          <Card>
            <CardHeader className="bg-gradient-to-r from-gray-50 to-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gray-100">
                    <Package className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Rapport Consolidé 360°</CardTitle>
                    <CardDescription>Vue complète de toute l'activité de la période</CardDescription>
                  </div>
                </div>
                <Button 
                  className="bg-gray-900 hover:bg-gray-800"
                  onClick={() => handleDownload('consolidated', 'pdf')}
                  disabled={downloading['consolidated-pdf']}
                  data-testid="download-consolidated-pdf"
                >
                  {downloading['consolidated-pdf'] ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Télécharger le rapport complet
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Dossier d'analyse financière (LLM-ready) — personnel interne uniquement */}
          {canSeeFinancialData && (
          <Card className="mt-6 border-emerald-200" data-testid="financial-dataset-card">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-emerald-100 shrink-0">
                    <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div className="min-w-0">
                    <CardTitle className="text-lg">Dossier d'analyse financière (Excel)</CardTitle>
                    <CardDescription>
                      Toutes les données financières et opérationnelles de la période, structurées
                      <strong> et brutes</strong> : compte de résultat, poste client et balance âgée,
                      trésorerie (banque + Wave), rentabilité par client / transporteur / axe / type de camion,
                      conformité FNE, plus le détail ligne à ligne. Une feuille contient le
                      <strong> prompt d'analyse prêt à coller</strong> dans ChatGPT, Claude ou Gemini.
                    </CardDescription>
                  </div>
                </div>
                <div className="flex flex-col gap-3 shrink-0">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="dataset-anonymize"
                      checked={anonymizeDataset}
                      onCheckedChange={(v) => setAnonymizeDataset(!!v)}
                      data-testid="dataset-anonymize"
                    />
                    <Label htmlFor="dataset-anonymize" className="text-xs font-normal cursor-pointer text-gray-600">
                      Anonymiser (clients C001, transporteurs T001…)
                    </Label>
                  </div>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleDownloadDataset}
                    disabled={downloading['financial-dataset']}
                    data-testid="download-financial-dataset"
                  >
                    {downloading['financial-dataset'] ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Télécharger le dossier Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
          )}

          {/* Analyse financière IA — personnel interne uniquement */}
          {canSeeFinancialData && (
            <FinancialAiAnalysisCard
              periodParams={
                dateMode === 'custom' && customFrom && customTo
                  ? { start_date: format(customFrom, 'yyyy-MM-dd'), end_date: format(customTo, 'yyyy-MM-dd') }
                  : { year: selectedPeriod?.year, month: selectedPeriod?.month }
              }
              periodLabel={getPeriodLabel()}
              canConfigure={canConfigureFinancial}
            />
          )}

          {/* Analyse opérationnelle IA — personnel interne uniquement */}
          {canSeeOperationalData && (
            <OperationalAiAnalysisCard
              periodParams={
                dateMode === 'custom' && customFrom && customTo
                  ? { start_date: format(customFrom, 'yyyy-MM-dd'), end_date: format(customTo, 'yyyy-MM-dd') }
                  : { year: selectedPeriod?.year, month: selectedPeriod?.month }
              }
              periodLabel={getPeriodLabel()}
              canConfigure={canConfigureFinancial}
            />
          )}

          {/* Analyse commerciale IA — direction uniquement */}
          {canSeeCommercialReport && (
            <CommercialAiAnalysisCard
              periodParams={
                dateMode === 'custom' && customFrom && customTo
                  ? { start_date: format(customFrom, 'yyyy-MM-dd'), end_date: format(customTo, 'yyyy-MM-dd') }
                  : { year: selectedPeriod?.year, month: selectedPeriod?.month }
              }
              periodLabel={getPeriodLabel()}
              canConfigure={canSeeCommercialReport}
              onSelectPeriod={({ year, month }) => {
                setDateMode('monthly');
                setSelectedPeriod({ year, month });
              }}
            />
          )}

          {/* Historical Reports */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Historique des rapports</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {periods.slice(1).map((period) => (
                <Card 
                  key={`${period.year}-${period.month}`}
                  className="cursor-pointer hover:border-emerald-300 transition-colors"
                  onClick={() => { setDateMode('monthly'); setSelectedPeriod(period); }}
                >
                  <CardContent className="p-3 text-center">
                    <p className="text-sm font-medium text-gray-900">{period.label}</p>
                    <div className="flex justify-center gap-1 mt-2">
                      <button 
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDateMode('monthly');
                          setSelectedPeriod(period);
                          setTimeout(() => handleDownloadPerformance(), 100);
                        }}
                      >
                        <FileText className="w-4 h-4 text-gray-400" />
                      </button>
                      <button 
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDateMode('monthly');
                          setSelectedPeriod(period);
                          setTimeout(() => handleDownload('operations', 'csv'), 100);
                        }}
                      >
                        <FileSpreadsheet className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              Envoyer le rapport par email
            </DialogTitle>
            <DialogDescription>
              Le rapport de performance sera envoyé avec les pièces jointes PDF et CSV.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Période</Label>
              <div className="text-sm font-medium text-gray-900 p-2 bg-gray-50 rounded-lg">
                {getPeriodLabel()}
              </div>
            </div>
            
            {(selectedCompany !== 'all' || selectedPartner !== 'all') && (
              <div className="space-y-2">
                <Label>Partenaire</Label>
                <div className="text-sm font-medium text-emerald-700 p-2 bg-emerald-50 rounded-lg">
                  {getSelectedLabel()}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="recipients">Destinataires *</Label>
              <Input
                id="recipients"
                placeholder="email1@exemple.com, email2@exemple.com"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                data-testid="email-recipients-input"
              />
              <p className="text-xs text-gray-500">
                Séparez les adresses par des virgules ou des espaces
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include-csv"
                checked={includeCSV}
                onCheckedChange={setIncludeCSV}
              />
              <Label htmlFor="include-csv" className="text-sm font-normal cursor-pointer">
                Inclure le fichier CSV (données brutes)
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailRecipients.trim()}
              data-testid="confirm-send-email"
            >
              {sendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
